import React, { useState } from "react";
import {
  Color,
  ColorInputWrapper,
  StyledColorpicker,
  StyledPopover,
  Wrapper,
} from "./ColorPicker.styled";
import Text from "../Text";
import { isValidColorpickerColor } from "./utils";
import InputAdornment from "../InputAdornment";
import { TextProps } from "../Text/Text";
import { WithTesting } from "../../types";
import SavedColors from "./savedColors/SavedColors";
import IconButton from "../IconButton";
import {
  HexAlphaColorPicker,
  HexColorPicker,
  RgbaStringColorPicker,
  RgbStringColorPicker,
} from "react-colorful";
import { ColorPickerBaseProps } from "react-colorful/dist/types";

type ColorPickerProps = WithTesting & {
  idPrefix: string;
  color?: string;
  onColorChange?: (state: { value: string; isValid: boolean }) => void;
  textProps?: TextProps & { ref?: React.Ref<HTMLInputElement> };
  predefinedColors?: string[];
  colorpickerMode?: SupportedColorPickerModes;
};

// see supported modes in react-colorful https://github.com/omgovich/react-colorful#supported-color-models
export const COLORPICKER_MODES = Object.freeze({
  HEX: "hex",
  HEX_ALPHA: "hex-alpha",
  RGB: "rgb",
  RGBA: "rgba",
});

export type SupportedColorPickerModes =
  (typeof COLORPICKER_MODES)[keyof typeof COLORPICKER_MODES];

const DEFAULT_COLOR = "#000000";

const ColorpickerComponentsMap: Record<
  SupportedColorPickerModes,
  (props: Partial<ColorPickerBaseProps<string>>) => JSX.Element
> = {
  [COLORPICKER_MODES.HEX_ALPHA]: HexAlphaColorPicker,
  [COLORPICKER_MODES.HEX]: HexColorPicker,
  [COLORPICKER_MODES.RGB]: RgbStringColorPicker,
  [COLORPICKER_MODES.RGBA]: RgbaStringColorPicker,
};

export function isWithAlphaMode(colorpickerMode: SupportedColorPickerModes) {
  return (
    [
      COLORPICKER_MODES.HEX_ALPHA,
      COLORPICKER_MODES.RGBA,
    ] as SupportedColorPickerModes[]
  ).includes(colorpickerMode);
}

const ColorPicker = ({
  idPrefix,
  color = DEFAULT_COLOR,
  onColorChange,
  textProps,
  dataTestId,
  predefinedColors,
  colorpickerMode = COLORPICKER_MODES.HEX_ALPHA,
}: ColorPickerProps) => {
  const [value, setValue] = useState(color);
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);

  const onChange = (newValue: string) => {
    onColorChange?.({
      value: newValue,
      isValid: isValidColorpickerColor(
        newValue,
        isWithAlphaMode(colorpickerMode),
      ),
    });
    setValue(newValue);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const openPopover = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const isSupportEyeDropper = "EyeDropper" in window;

  const onOpenEyeDropper = async () => {
    if (!isSupportEyeDropper) {
      return;
    }

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      onChange(result.sRGBHex);
    } catch (err) {
      // The user escaped the eyedropper mode.
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? `${idPrefix}-popover` : undefined;

  const ColorpickerCompnent = ColorpickerComponentsMap[colorpickerMode];

  return (
    <>
      <StyledPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        role="dialog"
      >
        <Wrapper data-testid={dataTestId}>
          <StyledColorpicker>
            <ColorpickerCompnent color={value} onChange={onChange} />
          </StyledColorpicker>
          <ColorInputWrapper>
            {isSupportEyeDropper && (
              <IconButton
                name="colorize"
                //TODO: Consult with designers what should be the IconButton type here and replace
                type="UNSAFE_transparent"
                onClick={onOpenEyeDropper}
                title="Pick a color"
              />
            )}
            <Text
              value={value}
              onChange={onInputChange}
              dataTestId={`manual-${dataTestId}`}
              withoutClearIcon
            />
          </ColorInputWrapper>
          <SavedColors
            predefinedColors={predefinedColors}
            colorpickerMode={colorpickerMode}
            currentValue={value}
            onChange={onChange}
            dataTestId={dataTestId}
          />
        </Wrapper>
      </StyledPopover>

      <Text
        aria-describedby={id}
        value={value}
        onChange={onInputChange}
        onClick={openPopover}
        startAdornment={
          <InputAdornment position="start" aria-hidden="true">
            <Color color={value} size="m" />
          </InputAdornment>
        }
        withoutClearIcon
        {...textProps}
      />
    </>
  );
};

export default ColorPicker;
